import { Box, Grid, GridItem, Skeleton, Spinner, Text } from '@chakra-ui/react';
import { AuthActionTypes, getUser, getOrgId } from '@devd-client/devd/auth';
import {
  PageHeader,
  PageToolbar,
  TeamMenu,
  MetaTags,
  MetricInfoItems,
  MetricDashboard,
  useAiAssistantStore,
  Loader,
  DateFilter,
  RepoDto,
} from '@devd-client/devd/components';
import { FC, useEffect, useState } from 'react';
import { Chatbox } from '../components/Chatbox';
import { Select } from 'chakra-react-select';
import { useCodeQualityDashboard } from '../hooks/useCodeQualityDashboard';
import { QualityThresholdItem } from '../helpers/home.types';

const filterOptions = [
  { value: 'team', label: 'Team' },
  { value: 'project', label: 'Project' },
];

export const Quality: FC = () => {
  const { isOpen } = useAiAssistantStore();
  const [inputWidth, setInputWidth] = useState('220px');

  const {
    filterBy,
    setFilterBy,
    selected,
    selectedTeam,
    setSelectedTeam,
    appState,
    dispatch,
    repos,
    setRepos,
    repoList,
    repoLoading,
    isLargerThan1820,
    isLargeThan2120,
    QUERY_KEYS,
    data,
    isFetching,
    type,
    setType,
    clickedTab,
    setClickedTab,
  } = useCodeQualityDashboard();

  useEffect(() => {
    if (repoList?.dto?.length) {
      const longestLabel = repoList.dto.reduce(
        (max: string, repo: RepoDto) =>
          repo.name.length > max.length ? repo.name : max,
        ''
      );

      const approximateWidth = longestLabel.length * 8 + 60;
      setInputWidth(`${Math.min(approximateWidth, 800)}px`);
    }
  }, [repoList]);
  return (
    <>
      <MetaTags title="Code Quality | Dashboard | Devdynamics" />

      <Box display={'flex'} flexDirection={'column'} width={'100%'}>
        <Box>
          <PageHeader
            headingText={'Code Quality'}
            mb={'10px'}
            subHeadingText="Data for main branch. Based on metadata from the integrated Code Quality tools "
            tooltip="Provides comprehensive metrics to assess and monitor your codebase health. Track test coverage, duplicate code density, security vulnerabilities, and code smells across your repositories. Compare metrics against defined goals, identify areas for improvement, and monitor progress over time. Use this dashboard to enforce coding standards, reduce technical debt, and ensure your team is building maintainable, secure software."
            // helpDocLink={helpDocLink}
          />
          <PageToolbar
            leftComp={
              <>
                <Box mr={3}>
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    fontWeight="semibold"
                    color="text.secondary"
                    lineHeight="17px"
                    mb={2}
                  >
                    Filter By
                  </Text>
                  <Select
                    placeholder="Select"
                    useBasicStyles
                    onChange={(e: any) => {
                      setFilterBy(e);
                    }}
                    size="sm"
                    value={filterBy}
                    options={filterOptions}
                  />
                </Box>
                {filterBy.value === 'team' && (
                  <TeamMenu
                    selectedTeam={selectedTeam}
                    handleSelectedTeam={setSelectedTeam}
                    AuthActionTypes={AuthActionTypes}
                    getUser={getUser}
                    appState={appState}
                    dispatch={dispatch}
                  />
                )}

                {filterBy.value === 'project' && (
                  <Box mr={3} maxWidth="800px" width={inputWidth}>
                    <Text
                      fontSize="sm"
                      fontFamily="heading"
                      fontWeight="semibold"
                      color="text.secondary"
                      lineHeight="17px"
                      mb={2}
                    >
                      Project
                    </Text>
                    <Select
                      placeholder="Select Projects"
                      isMulti={true}
                      useBasicStyles
                      onChange={(e: any) => {
                        setRepos(e);
                      }}
                      size="sm"
                      value={repos}
                      options={repoList?.dto?.map((repo: RepoDto) => ({
                        value: repo.name,
                        label: repo.name,
                      }))}
                    />
                  </Box>
                )}
              </>
            }
            rightComp={
              <DateFilter
                getUser={getUser}
                appState={appState}
                dispatch={dispatch}
                AuthActionTypes={AuthActionTypes}
              />
            }
          />
        </Box>

        <Grid
          my={4}
          templateColumns={
            isLargeThan2120
              ? 'repeat(6, 1fr)'
              : isLargerThan1820
              ? 'repeat(5, 1fr)'
              : 'repeat(4, 1fr)'
          }
          gap={4}
        >
          {isFetching
            ? Array(isLargeThan2120 ? 6 : isLargerThan1820 ? 5 : 4)
                .fill(null)
                .map((_, index) => (
                  <Box
                    key={index}
                    position={'relative'}
                    minWidth="305px"
                    height="109px"
                  >
                    <Skeleton minWidth="305px" height="109px" key={index} />
                    <Spinner position={'absolute'} top={'40%'} left={'43%'} />
                  </Box>
                ))
            : data?.map(
                (item: QualityThresholdItem, idx: number) =>
                  (item.chartKey !== 'QUALITY_SECURITY_HOTSPOTS' ||
                    getOrgId() !== 'OIDPPE') && (
                    <GridItem key={idx}>
                      <MetricInfoItems
                        item={item}
                        setType={setType}
                        clickedTab={clickedTab}
                        setClickedTab={setClickedTab}
                      />
                    </GridItem>
                  )
              )}
        </Grid>

        <MetricDashboard
          selected={selected}
          selectedTeam={filterBy.value === 'project' ? '' : selectedTeam.value}
          setType={setType}
          type={type}
          queryKey={QUERY_KEYS.allQualityThresholds}
          repos={
            filterBy.value === 'team'
              ? []
              : repos.map((el: { value: string; label: string }) => el.value)
          }
        />
      </Box>
      {isOpen && <Chatbox />}
      {repoLoading && <Loader />}
    </>
  );
};

export default Quality;
